import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
  useGetBankAccountsQuery,
  useGetBankReconciliationsQuery,
} from '../../../store/api/financeApi';

function BankReconciliationList() {
  const navigate = useNavigate();
  const { data: bankAccounts = [] } = useGetBankAccountsQuery();
  const [selectedBankAccountId, setSelectedBankAccountId] = useState('');

  const { data: reconciliations = [], isLoading } = useGetBankReconciliationsQuery(selectedBankAccountId, {
    skip: !selectedBankAccountId,
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Bank Reconciliation
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/finance/bank-reconciliation/new')}
        >
          New Reconciliation
        </Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <FormControl sx={{ minWidth: 300 }}>
            <InputLabel>Select Bank Account</InputLabel>
            <Select
              value={selectedBankAccountId}
              label="Select Bank Account"
              onChange={(e) => setSelectedBankAccountId(e.target.value)}
            >
              {bankAccounts.map((acc) => (
                <MenuItem key={acc.id} value={acc.id}>
                  {acc.name} {acc.accountNumber ? `(${acc.accountNumber})` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {selectedBankAccountId && (
        <Card>
          <CardContent>
            {isLoading ? (
              <Typography color="text.secondary">Loading...</Typography>
            ) : reconciliations.length === 0 ? (
              <Typography color="text.secondary">No reconciliations found for this account.</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Statement Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Opening Balance</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Closing Balance</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Created By</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reconciliations.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>{new Date(row.statementDate).toLocaleDateString()}</TableCell>
                        <TableCell>{Number(row.openingBalance).toFixed(2)}</TableCell>
                        <TableCell>{Number(row.closingBalance).toFixed(2)}</TableCell>
                        <TableCell>
                          <Chip 
                            label={row.status === 'completed' ? 'Completed' : 'Draft'} 
                            color={row.status === 'completed' ? 'success' : 'warning'} 
                            size="small" 
                          />
                        </TableCell>
                        <TableCell>{row.creator?.name || 'System'}</TableCell>
                        <TableCell align="right">
                          <Button 
                            size="small" 
                            variant="outlined"
                            onClick={() => navigate(`/finance/bank-reconciliation/${row.id}`)}
                          >
                            {row.status === 'completed' ? 'View' : 'Resume'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

export default BankReconciliationList;
